import individualWorkSupportUtil from '../../../../../share/util/individualWorkSupportUtil';
import session from '../../../../../share/session';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../../share/util/api/polling';
import { checkIsCompressed } from './checkIsCompressed';
import { terminate } from './terminate';
import { PHASE } from './constants';
const moment = require('moment');

/**
 * 選択された項目("image_type")のすべてのimage_order"についてreadApiを実行する
 * @param {object} self this インスタンス
 */
export async function readSelectedItems(self) {
  try {
    self.isLoading = true;
    // 圧縮有無確認を実施
    await checkIsCompressed(self);
    // 読み出すDIDの総数を算出
    self.totalDidCount = self.selectedHistoryListItems.reduce((total, result) => {
      const historiesDidCount = result.histories.reduce((sum, history) => sum + history.did_count, 0);
      return total + historiesDidCount;
    }, 0);
    // totalDidCountを算出してから画面遷移
    self.phase = PHASE.READ;
    for (const item of self.selectedHistoryListItems) {
      for (const historyInfo of item.histories) {
        // 配列にis_preprocess_failedの値を記録する
        historyInfo.is_preprocess_failed = await read(self, item.image_type, historyInfo);
      }
    }
    // 各履歴について、識別子と識別子に応じた画像データ、制御データの合計値を配列に格納する
    const headerInfo = [];
    self.selectedHistoryListItems.forEach(result => {
      const historyId = result.history_id;
      const dataSize = result.data_size;
      result.histories.forEach((historyInfo) => {
        // 復号/解凍に成功している履歴のみ、追加する
        if (!historyInfo.is_preprocess_failed) {
          headerInfo.push({
            history_id: historyId,
            // 3Byteに成形
            data_size: dataSize.toString(16).toUpperCase().padStart(6, '0')
          });
        }
      });
    });
    // 読み出すDIDの総サイズを算出
    // 4Byteに成形
    const totalImageDataSize = headerInfo.reduce((sum, history) => {
      return sum + parseInt(history.data_size, 16);
    }, 0).toString(16).toUpperCase().padStart(8, '0');
    await makeDownloadFile(self, totalImageDataSize, headerInfo);
    // 各履歴のis_preprocess_failedによって画面分岐
    const flags = (self.selectedHistoryListItems).flatMap(item =>
      (item.histories).map(h => h.is_preprocess_failed)
    );
    if (flags.every(v => v)) {
      await terminate(self, PHASE.SAVE_FAILED);
    } else if (flags.some(v => v)) {
      self.phase = PHASE.PARTIAL_FAILED;
    } else {
      await downloadFile(self);
    }
  } catch (error) {
    await self.validateErrorResult(error);
  } finally {
    self.isLoading = false;
  }
}

/**
 * 履歴データ読み出し
 * @param {object} self this インスタンス
 * @param {string} imageType 種別
 * @param {object} historyInfo 履歴情報(image_order, did_count, total_data_size, total_image_control_data_size)
 * @returns {boolean} 履歴に対するuser_data.is_preprocess_failedの値
 */
async function read(self, imageType, historyInfo) {
  try {
    const token = session.getToken();
    const response = await individualWorkSupportUtil.pollingWspApi(
      self,
      self.netAppId,
      self.systemId,
      self.workSupportId,
      {
        id_token: token.id_token,
        is_special: true,
        type: 'read',
        image_type: imageType,
        image_order: historyInfo?.image_order,
        download_uuid: self.downloadUuid,
        is_compressed: self.isCompressed,
      },
      // エラー発生のコールバック
      async (errorType) => { errorType; return ''; },
      // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
      async () => { return ''; },
      // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
      async () => { return ''; }
    );

    if (response) {
      switch (response.data.status) {
        case REQUEST_STATUS_COMPLETED:
          // 画面に表示する進捗を更新
          self.processedDidCount += historyInfo?.did_count || 0;
          self.progress = (self.processedDidCount / self.totalDidCount) * 100;
          self.downloadUuid = response.data.user_data.download_uuid;
          return response.data.user_data.is_preprocess_failed;
        case REQUEST_STATUS_FAILED:
        default:
          throw new Error("Request failed");
      }
    } else {
      throw new Error("No response received");
    }
  } catch {
    throw new Error("No response received");
  }
}

/**
 * ファイルダウンロード
 * @param {object} self this インスタンス
 */
export async function downloadFile(self) {
  try {
    self.isLoading = true;
    if (!self.fileUrl) {
      self.showUnexpectedError();
    }
    // ファイル名を変更
    const formattedDate = moment().format("YYYYMMDDHHmmss");

    const response = await fetch(self.fileUrl);
    if (response) {
      const myBlob = await response.blob();
      const objUrl = URL.createObjectURL(myBlob);
      const link = document.createElement("a");
      link.href = objUrl;
      link.download = `${formattedDate}.${self.fileExtension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objUrl);
      // 成功画面に遷移
      await terminate(self, PHASE.SUCCESS);
    }
  } catch (error) {
    await self.showUnexpectedError();
  } finally {
    self.isLoading = false;
  }
}

/**
 * 出力ファイル作成
 * @param {object} self this インスタンス
 * @param {string} totalImageDataSize ヘッダーに使用する暗号化前のデータサイズ
 * @param {object} headerInfo ヘッダーに使用する各履歴の識別子とデータサイズ
 * @returns {boolean} 履歴に対するuser_data.is_preprocess_failedの値
 */
async function makeDownloadFile(self, totalImageDataSize, headerInfo) {
  try {
    const token = session.getToken();
    const response = await individualWorkSupportUtil.pollingWspApi(
      self,
      self.netAppId,
      self.systemId,
      self.workSupportId,
      {
        id_token: token.id_token,
        is_special: true,
        type: 'make_download_file',
        download_uuid: self.downloadUuid,
        total_image_data_size: totalImageDataSize,
        header_info: headerInfo
      },
      // エラー発生のコールバック
      async (errorType) => { errorType; return ''; },
      // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
      async () => { return ''; },
      // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
      async () => { return ''; }
    );

    if (response) {
      switch (response.data.status) {
        case REQUEST_STATUS_COMPLETED:
          self.fileUrl = response.data.user_data.file_url;
          self.fileExtension = response.data.user_data.file_extension;
          break;
        case REQUEST_STATUS_FAILED:
        default:
          throw new Error("Request failed");
      }
    } else {
      throw new Error("No response received");
    }
  } catch {
    throw new Error("No response received");
  }
}